import { useState } from 'react';
import { MenuProps } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import { SavedQueryType } from '../../../../types';
import {
  SaveQueryButtonPropsType,
  SaveQueryModalPropsType,
} from '../../components';
import { openDeleteFavoriteConfirmModal } from './helpers';

export type UseFavoritesDropdownParamsType = {
  savedQueries: SavedQueryType[];
  activeFavorite?: SavedQueryType;
  onSelect: (favoriteId: string) => void;
  onDelete: (favoriteId: string) => void;
  saveDisabled: boolean;
  isSaveActive: boolean;
  onSave: (name: string) => void;
};

export type UseFavoritesDropdownReturnType = {
  favoritesMenuProps: MenuProps;
  savedQueries: SavedQueryType[];
  activeFavorite?: SavedQueryType;
  handleFavoriteSelect: (favoriteId: string) => void;
  handleDeleteFavorite: (favoriteId: string) => void;
  saveButtonProps: SaveQueryButtonPropsType;
  saveModalProps: SaveQueryModalPropsType;
};

export const useFavoritesDropdown = ({
  savedQueries,
  activeFavorite,
  onSelect,
  onDelete,
  saveDisabled,
  isSaveActive,
  onSave,
}: UseFavoritesDropdownParamsType): UseFavoritesDropdownReturnType => {
  const { t } = useTranslation();
  const [favoritesOpened, { open: openFavorites, close: closeFavorites }] =
    useDisclosure(false);
  const [saveModalOpened, setSaveModalOpened] = useState(false);
  const [saveQueryName, setSaveQueryName] = useState('');

  const handleFavoriteSelect = (favoriteId: string) => {
    onSelect(favoriteId);
    closeFavorites();
  };

  const handleDeleteFavorite = (favoriteId: string) => {
    const favorite = savedQueries.find(
      (savedQuery) => savedQuery.id === favoriteId
    );

    if (!favorite) return;

    openDeleteFavoriteConfirmModal({
      t,
      onConfirm: () => onDelete(favoriteId),
    });
  };

  const handleOpenSaveModal = () => {
    setSaveQueryName('');
    setSaveModalOpened(true);
  };

  const handleCloseSaveModal = () => {
    setSaveModalOpened(false);
    setSaveQueryName('');
  };

  const handleConfirmSaveQuery = () => {
    if (!saveQueryName.trim()) return;

    onSave(saveQueryName.trim());
    handleCloseSaveModal();
  };

  const handleClickSaveButton = () => {
    if (saveDisabled) return;
    if (isSaveActive) return;
    if (activeFavorite) return;

    handleOpenSaveModal();
  };

  return {
    favoritesMenuProps: {
      opened: favoritesOpened,
      onChange: (opened) => (opened ? openFavorites() : closeFavorites()),
      position: 'bottom-start',
      withinPortal: false,
    },
    savedQueries,
    activeFavorite,
    handleFavoriteSelect,
    handleDeleteFavorite,
    saveButtonProps: {
      disabled: saveDisabled,
      isSaveActive,
      onClick: handleClickSaveButton,
    },
    saveModalProps: {
      opened: saveModalOpened,
      saveQueryName,
      onClose: handleCloseSaveModal,
      onSaveQueryNameChange: setSaveQueryName,
      onConfirm: handleConfirmSaveQuery,
    },
  };
};
